import React, { useEffect, useState } from "react";

function StatusBadge({ node }) {
  const status = (() => {
    if (!node.people?.length) return "vacant";
    if (node.people.every((p) => p.is_placeholder)) return "placeholder";
    return "filled";
  })();

  const styles = {
    vacant:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    placeholder:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900",
    filled:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
  };

  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function PersonChip({ person }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-neutral-700 px-2 py-2 bg-white dark:bg-neutral-950">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="font-medium text-[11px] text-black dark:text-white">
          {person.display_name || person.full_name || "-"}
        </span>

        {person.is_placeholder ? (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-yellow-50 text-yellow-700 border-yellow-200">
            placeholder
          </span>
        ) : (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">
            real
          </span>
        )}

        <span className="text-[9px] px-1.5 py-0.5 rounded-full border bg-black text-white border-black">
          {person.is_primary ? "primary" : person.assignment_type || "secondary"}
        </span>
      </div>

      <div className="text-[10px] text-gray-500 mt-1 truncate">
        {person.email_work || "-"}
      </div>
    </div>
  );
}

export default function HROrgChartNode({
  node,
  compact = false,
  onSelect,
  expanded,
}) {
  const [localExpanded, setLocalExpanded] = useState(true);

  useEffect(() => {
    if (typeof expanded === "boolean") {
      setLocalExpanded(expanded);
    }
  }, [expanded]);

  const isExpanded =
    typeof expanded === "boolean" ? expanded : localExpanded;

  const visiblePeople = isExpanded ? node.people : node.people.slice(0, 2);

  return (
    <div
      onClick={onSelect}
      className={`w-[260px] min-w-[260px] max-w-[260px] bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-sm cursor-pointer transition ${
        compact ? "hover:border-blue-400 hover:shadow-md" : ""
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold">{node.position_title}</h3>

              <StatusBadge node={node} />

              {node.is_leadership && (
                <span className="text-[10px] px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700">
                  leadership
                </span>
              )}
            </div>

            <div className="text-[10px] text-gray-500 mt-1">
              {node.position_code}
            </div>

            <div className="text-[10px] text-gray-500 mt-1">
              {node.department_name || "-"} • Planned: {node.planned_headcount || 1}
            </div>

            <div className="text-[10px] text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
  <span>People: {node.totalSubtreePeopleCount || 0}</span>
<span>Positions: {node.totalSubtreePositionsCount || 1}</span>
</div>

            {node.reports_to_position_title ? (
              <div className="text-[10px] text-gray-400 mt-1">
                Reports to: {node.reports_to_position_title}
              </div>
            ) : null}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setLocalExpanded((v) => !v);
            }}
            className="px-2 py-1 text-[10px] border rounded"
          >
            {isExpanded ? "Hide" : "Show"}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-3">
            <div className="text-[10px] uppercase text-gray-400 mb-2">
              People
            </div>

            {node.people?.length ? (
              <div className="space-y-2">
                {visiblePeople.map((p) => (
                  <PersonChip
                    key={`${node.position_id}-${p.person_id}-${p.assignment_type}`}
                    person={p}
                  />
                ))}
              </div>
            ) : (
              <div className="text-[10px] text-gray-400">
                No people
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}